/**
 * Medarch Design System Tokens
 *
 * Auto-generated from Figma: Medarch-DS v.0.1 (Variables-DS_rev-1.json)
 * 3-tier token architecture: Primitive → Alias → Component
 *
 * NEVER hardcode hex values in components — always import from here.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 1: PRIMITIVE TOKENS — Raw values (hex colors, spacing, typography)
// ═══════════════════════════════════════════════════════════════════════════════

export const PRIMITIVE = {
  color: {
    brandBlue: {
      25: "#EEF7FF",
      50: "#E5F3FF",
      100: "#AEDAFF",
      200: "#82BEF2",
      300: "#5DA2DD",
      400: "#4088C6",
      500: "#2771B1",
      600: "#175D9A",
      700: "#104E84", // Brand primary
      800: "#0B406E",
      900: "#08345A",
      1000: "#092A47",
    },
    lightBlue: {
      "00": "#F2F7FF",
      50: "#E0EDFF",
      100: "#BDDAFF",
      200: "#86B8FE",
      300: "#5598F6",
      400: "#2D7AE5",
      500: "#105FCE",
      550:'#106DCC',
      600: "#004AB1",
      700: "#003B8D",
      800: "#002962",
      900: "#001532",
    },
    darkBlue: {
      50: "#F2F4FF",
      100: "#ECEFF4",
      200: "#D2DAE3",
      300: "#ADBBCD",
      400: "#7E95B0",
      500: "#486990",
      600: "#0F3A6D",
      700: "#0D315E",
      800: "#0B2750",
      900: "#091D40",
      1000: "#071331",
    },
    neutrals: {
      "000": "#FFFFFF",
      100: "#FCFCFC",
      200: "#FAFAFA",
      300: "#F7F7F7",
      400: "#F5F5F5",
      500: "#F2F2F2",
      550: "#F2F6F8",
      600: "#F0F0F0",
      700: "#EDEDED",
      800: "#EBEBEB",
      900: "#E6E6E6",
      1000: "#E0E0E0",
      1100: "#DBDBDB",
      1200: "#CCCCCC",
      1300: "#C7C7C7",
      1400: "#C2C2C2",
      1500: "#B3B3B3",
      1600: "#A8A8A8",
      1700: "#9E9E9E",
      1800: "#8F8F8F",
      1900: "#808080",
      2000: "#7A7A7A",
      2100: "#666666",
      2200: "#525252",
      2300: "#3D3D3D",
      2400: "#292929",
      2500: "#1A1A1A",
      2600: "#0D0D0D",
      2700: "#000000",
    },
    green: {
      "00": "#E8FCE5",
      50: "#E1FCDE",
      100: "#CDF9CA",
      200: "#A4F3A3",
      300: "#7EEB83",
      400: "#42D75B",
      500: "#17BF33",
      600: "#049B22",
      700: "#016A1C",
      800: "#005016",
      900: "#00300F",
    },
    red: {
      "00": "#FFF2F3",
      50: "#FFD4D8",
      100: "#FFB6BC",
      200: "#FA7D87",
      300: "#F14A58",
      400: "#E42131",
      500: "#CE0718",
      600: "#B1000F",
      700: "#8D000C",
      800: "#620008",
      900: "#320004",
    },
    orange: {
      "00": "#FFFBF2",
      50: "#FFF2D2",
      100: "#FFE8B1",
      200: "#FFCF73",
      300: "#FCB33B",
      400: "#F2930D",
      500: "#D66F00",
      600: "#BA5900",
      700: "#943C00",
      800: "#662100",
      900: "#340D00",
    },
    teal: {
      "00": "#EAFAF7",
      50: "#D6F5F0",
      100: "#C2EFE8",
      200: "#AEEAE0",
      300: "#85E0D1",
      400: "#5CD6C2",
      500: "#33CCB3",
      600: "#29A38F",
      700: "#1F7A6B",
      800: "#155147",
      900: "#0B2924",
    },
    violet: {
      "00": "#EDE9FB",
      50: "#DBD4F7",
      100: "#B7A9EF",
      200: "#937EE7",
      300: "#6F53DF",
      400: "#4B28D7",
      500: "#3C20AC",
      600: "#2D1881",
      700: "#25146C",
      800: "#1E1056",
      900: "#0F082B",
    },
    purple: {
      "00": "#F9E7FD",
      50: "#F4D0FB",
      100: "#F1C2FA",
      200: "#E9A1F7",
      300: "#DE71F4",
      400: "#D342F0",
      500: "#C813EC",
      600: "#A00FBD",
      700: "#780B8E",
      800: "#50085E",
      900: "#BC12DE",
    },
    pink: {
      "00": "#FDE7F4",
      50: "#FCCFEA",
      100: "#F8A0D5",
      200: "#F570C0",
      300: "#F141AB",
      400: "#F029A0",
      500: "#D60F87",
      600: "#BE0E78",
      700: "#8F0A5A",
      800: "#5F073C",
      900: "#47052D",
    },
  },

  spacing: {
    0: 0,
    1: 1,
    1.5: 1.5,
    2: 2,
    4: 4,
    8: 8,
    12: 12,
    16: 16,
    20: 20,
    24: 24,
    28: 28,
    32: 32,
    36: 36,
    40: 40,
    44: 44,
    48: 48,
    52: 52,
    56: 56,
    60: 60,
    64: 64,
    80: 80,
    100: 100,
    120: 120,
  } as const,

  radius: {
    0: 0,
    2: 2,
    4: 4,
    8: 8,
    12: 12,
    16: 16,
    20: 20,
    24: 24,
    max: 9999,
  } as const,

  typography: {
    fontFamily: "'Inter', sans-serif",
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
    },
    fontSize: {
      heading: { h1: 40, h2: 32, h3: 28, h4: 24 },
      title: { t1: 24, t2: 20, t3: 18, t4: 16, t5: 14, t6: 12 },
      body: { b1: 20, b2: 18, b3: 16, b4: 14, b5: 12, b6: 10 },
    },
    lineHeight: {
      "120%": {
        10: 12,
        12: 14,
        14: 18,
        16: 20,
        18: 22,
        20: 24,
        24: 30,
        28: 34,
        32: 38,
        40: 48,
      },
      "150%": {
        10: 14,
        12: 18,
        14: 20,
        16: 24,
        18: 26,
        20: 30,
        24: 36,
        28: 42,
        32: 48,
        40: 60,
      },
    },
  },

  shadow: {
    offset: {
      0: 0,
      100: 1,
      200: 2,
      300: 4,
      400: 8,
      500: 12,
      600: 16,
      700: 20,
      800: 24,
    },
    color: {
      light: "rgba(0, 0, 0, 0.05)",
      medium: "rgba(0, 0, 0, 0.15)",
      high: "rgba(0, 0, 0, 0.25)",
    },
  },

  overlay: {
    light: "rgba(0, 0, 0, 0.1)",
    medium: "rgba(0, 0, 0, 0.25)",
    dark: "rgba(0, 0, 0, 0.4)",
    smoke: "rgba(255, 255, 255, 0.4)",
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 2: ALIAS TOKENS — Semantic meanings referencing primitives
// ═══════════════════════════════════════════════════════════════════════════════

const c = PRIMITIVE.color;

export const ALIAS = {
  icon: {
    default: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    white: c.neutrals[100],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    semiDarkGrey: c.neutrals[2200],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2600],
  },

  text: {
    default: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    white: c.neutrals[100],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    semiDarkGrey: c.neutrals[2200],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2500],
  },

  semantics: {
    success: {
      bg: c.green[50],
      default: c.green[500],
      hover: c.green[600],
      pressed: c.green[700],
    },
    warning: {
      bg: c.orange[50],
      default: c.orange[500],
      hover: c.orange[600],
      pressed: c.orange[700],
    },
    error: {
      bg: c.red[50],
      default: c.red[500],
      hover: c.red[600],
      pressed: c.red[700],
    },
  },

  surface: {
    canvas: {
      blue: {
        z0: c.darkBlue[300],
        z1: c.darkBlue[200],
        z2: c.darkBlue[100],
        z3: c.darkBlue[50],
      },
      slate: {
        z0: c.neutrals[1000],
        z1: c.neutrals[700],
        z2: c.neutrals[400],
        z3: c.neutrals[200],
        z4: c.neutrals[100],
        z5: c.neutrals["000"],
      },
    },
    action: {
      primaryBg: {
        default: c.brandBlue[700],
        hover: c.brandBlue[800],
        pressed: c.brandBlue[900],
        focused: c.brandBlue[1000],
      },
      secondaryBg: {
        yellow: c.orange["00"],
        white: c.neutrals["000"],
        barelyGrey: c.neutrals[1200],
        lightGrey: c.neutrals[1800],
        midGrey: c.neutrals[2000],
        darkGrey: c.neutrals[2400],
        blue: c.brandBlue[25],
        hover: c.brandBlue[50],
        pressed: c.brandBlue[100],
      },
      input: {
        default: c.neutrals[200],
        selected: c.orange[50],
        disabled: c.neutrals[1000],
      },
    },
  },

  outline: {
    default: c.brandBlue[700],
    white: c.neutrals["000"],
    lightBlue: c.brandBlue[50],
    lightBlueHover: c.brandBlue[500],
    lightBluePressed: c.brandBlue[700],
    hover: c.brandBlue[800],
    pressed: c.brandBlue[900],
    focused: c.brandBlue[1000],
    barelyGrey: c.neutrals[1200],
    lightGrey: c.neutrals[1800],
    midGrey: c.neutrals[2000],
    darkGrey: c.neutrals[2400],
    black: c.neutrals[2600],
    yellow: c.orange[500],
    red: c.red[500],
    width: PRIMITIVE.spacing[1],
  },

  overlay: {
    light: PRIMITIVE.overlay.light,
    medium: PRIMITIVE.overlay.medium,
    dark: PRIMITIVE.overlay.dark,
    smoke: PRIMITIVE.overlay.smoke,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 3: COMPONENT TOKENS — Per-component design decisions
// ═══════════════════════════════════════════════════════════════════════════════

const s = PRIMITIVE.spacing;
const r = PRIMITIVE.radius;

export const COMPONENT = {
  button: {
    primary: {
      bg: {
        default: ALIAS.surface.action.primaryBg.default,
        hover: ALIAS.surface.action.primaryBg.hover,
        pressed: ALIAS.surface.action.primaryBg.pressed,
      },
      outline: { focused: ALIAS.outline.focused },
      icon: { default: ALIAS.icon.white },
      text: {
        default: ALIAS.text.white,
        hover: ALIAS.text.white,
        pressed: ALIAS.text.white,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[8], y: s[4], gap: s[4] },
      radius: { default: r[4], round: r.max },
    },
    secondary: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.white,
        hover: ALIAS.surface.action.secondaryBg.hover,
        pressed: ALIAS.surface.action.secondaryBg.pressed,
      },
      outline: {
        default: ALIAS.outline.default,
        hover: ALIAS.outline.hover,
        pressed: ALIAS.outline.hover,
        focused: ALIAS.outline.focused,
      },
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
      },
      text: {
        default: ALIAS.text.default,
        hover: ALIAS.text.hover,
        pressed: ALIAS.text.hover,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[8], y: s[4], gap: s[4] },
      radius: { default: r[4], round: r.max },
    },
    tertiary: {
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
      },
      text: {
        default: ALIAS.text.default,
        hover: ALIAS.text.hover,
        pressed: ALIAS.text.pressed,
        disabled: ALIAS.text.lightGrey,
      },
      padding: { x: s[0], y: s[0], gap: s[4] },
    },
    icon: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.blue,
        hover: ALIAS.surface.action.secondaryBg.hover,
        pressed: ALIAS.surface.action.secondaryBg.pressed,
      },
      outline: {
        default: ALIAS.outline.lightBlue,
        hover: ALIAS.outline.lightBlueHover,
        pressed: ALIAS.outline.lightBluePressed,
      },
      icon: {
        default: ALIAS.icon.default,
        hover: ALIAS.icon.hover,
        pressed: ALIAS.icon.pressed,
        disabled: ALIAS.icon.lightGrey,
      },
      padding: { x: s[8], y: s[8] },
      radius: r[4],
    },
    navbar: {
      top: {
        bg: { hover: ALIAS.surface.action.secondaryBg.hover },
        outline: { selected: ALIAS.outline.white, width: s[2] },
        icon: { default: ALIAS.icon.white },
        padding: { x: s[4], bottom: s[4] },
        radius: r[4],
        gap: s[2],
      },
      tertiary: {
        outline: { selected: ALIAS.outline.pressed, width: s[2] },
        icon: {
          default: ALIAS.icon.darkGrey,
          hover: ALIAS.icon.hover,
          selected: ALIAS.icon.pressed,
        },
        padding: { x: s[4], bottom: s[8] },
        gap: s[2],
      },
    },
  },

  inputField: {
    bg: {
      default: ALIAS.surface.action.input.default,
      selected: ALIAS.surface.action.input.selected,
      disabled: ALIAS.surface.action.input.disabled,
    },
    text: {
      default: ALIAS.text.lightGrey,
      hover: ALIAS.text.lightGrey,
      disabled: ALIAS.text.lightGrey,
      label: ALIAS.text.darkGrey,
    },
    outline: {
      default: ALIAS.outline.barelyGrey,
      hover: ALIAS.outline.lightBlueHover,
      selected: ALIAS.outline.yellow,
      error: ALIAS.outline.red,
      focused: ALIAS.outline.focused,
      disabled: ALIAS.outline.lightGrey,
    },
    padding: { x: s[12], y: s[8], gap2: s[2], gap4: s[4], gap8: s[8] },
    radius: { default: r[4], round: r.max },
  },

  checkbox: {
    iconBg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.hover,
      pressed: ALIAS.surface.action.secondaryBg.pressed,
    },
    text: { default: ALIAS.text.default, disabled: ALIAS.text.lightGrey },
    radius: r[4],
  },

  toggle: {
    iconBg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.hover,
      active: ALIAS.surface.action.secondaryBg.pressed,
    },
    text: {
      default: ALIAS.text.darkGrey,
      active: ALIAS.text.darkGrey,
      disabled: ALIAS.text.lightGrey,
    },
    outline: {
      default: ALIAS.outline.midGrey,
      hover: ALIAS.outline.default,
      active: ALIAS.outline.pressed,
    },
    radius: s[12],
    padding: s[4],
  },

  badge: {
    text: {
      default: ALIAS.text.white,
      neutral: ALIAS.text.white,
      positive: ALIAS.semantics.success.pressed,
      warning: ALIAS.semantics.warning.hover,
      negative: ALIAS.semantics.error.hover,
      appointmentStatus: {
        scheduled: c.orange[700],
        confirmed: c.brandBlue[800],
        instant: c.red[700],
        checkedIn: c.violet[600],
        inExam: c.teal[800],
        signedOff: c.green[800],
        noShow: c.neutrals[2400],
        cancelled: c.neutrals[2400],
      },
      appointmentType: {
        telehealth: c.pink[600],
        inPerson: c.neutrals[2400],
      },
    },
    bg: {
      default: ALIAS.surface.action.primaryBg.default,
      neutral: ALIAS.surface.action.secondaryBg.midGrey,
      positive: ALIAS.semantics.success.bg,
      warning: ALIAS.semantics.warning.bg,
      negative: ALIAS.semantics.error.bg,
      appointmentStatus: {
        scheduled: c.orange[100],
        confirmed: c.brandBlue[100],
        instant: c.red[100],
        checkedIn: c.violet[50],
        inExam: c.teal[50],
        signedOff: c.green[100],
        noShow: c.neutrals[1000],
        cancelled: c.neutrals[1700],
      },
      appointmentType: {
        telehealth: c.pink[50],
        inPerson: c.neutrals[1000],
      },
    },
    icon: {
      default: ALIAS.text.white,
      neutral: ALIAS.text.white,
      positive: ALIAS.semantics.success.pressed,
      warning: ALIAS.semantics.warning.hover,
      negative: ALIAS.semantics.error.hover,
    },
    radius: s[8],
    padding: { x: s[4], y: s[0] },
    gap: s[2],
  },

  tag: {
    text: {
      default: ALIAS.text.default,
      hover: ALIAS.text.hover,
      active: ALIAS.icon.white,
    },
    bg: {
      default: ALIAS.surface.action.secondaryBg.white,
      hover: ALIAS.surface.action.secondaryBg.pressed,
      active: ALIAS.surface.action.primaryBg.default,
    },
    outline: {
      default: ALIAS.outline.default,
      hover: ALIAS.outline.hover,
      filter: ALIAS.icon.pressed,
    },
    icon: {
      default: ALIAS.icon.default,
      hover: ALIAS.icon.hover,
      active: ALIAS.icon.white,
    },
    radius: r.max,
    padding: { x: s[8], y: s[4] },
    gap: s[4],
  },

  avatar: {
    text: ALIAS.text.darkGrey,
    bg: ALIAS.surface.canvas.slate.z0,
    radius: r.max,
    statusLive: ALIAS.semantics.success.default,
  },

  scrollbar: {
    default: ALIAS.surface.action.secondaryBg.barelyGrey,
    bg: ALIAS.surface.canvas.slate.z2,
    hover: ALIAS.surface.action.secondaryBg.lightGrey,
    pressed: ALIAS.surface.action.secondaryBg.midGrey,
    width: s[24],
    height: s[24],
  },

  table: {
    bg: {
      header: c.neutrals[1000],
      row1: ALIAS.surface.action.secondaryBg.white,
      row2: c.neutrals[400],
      lastRow1: ALIAS.surface.action.secondaryBg.white,
      lastRow2: c.neutrals[300],
      footer: ALIAS.surface.action.secondaryBg.white,
    },
    rowHeight: s[28],
    radius: r[4],
    outline: ALIAS.outline.barelyGrey,
    gap: s[4],
    padding: s[12],
  },

  navbar: {
    top: {
      bg: c.brandBlue[800],
      height: s[36],
      outline: ALIAS.outline.barelyGrey,
      gap: { 16: s[16], 20: s[20] },
    },
  },

  tab: {
    pinboard: {
      bg: {
        default: ALIAS.surface.action.secondaryBg.blue,
        hover: ALIAS.semantics.warning.bg,
        selected: ALIAS.semantics.warning.bg,
      },
      outline: {
        default: ALIAS.outline.lightGrey,
        hover: ALIAS.outline.default,
        selected: ALIAS.outline.default,
      },
      iconHoverSelected: ALIAS.icon.pressed,
      padding: { x: s[8], y: s[4] },
      radius: r[4],
      gap: s[4],
    },
  },

  card: {
    radius: r[4],
    bg: ALIAS.surface.canvas.slate.z2,
  },

  modal: {
    radius: r[8],
  },

  separator: {
    color: ALIAS.outline.barelyGrey,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN TOKENS — Login page (LoginV2)
// ═══════════════════════════════════════════════════════════════════════════════

export const LOGIN_TOKENS = {
  color: {
    /** Brand primary — headings, links, button bg, checkbox checked */
    primary: PRIMITIVE.color.brandBlue[700], // #104E84
    /** Primary button hover bg */
    primaryHover: "#0D3F6B",
    /** Welcome subheading text */
    subheadingText: "#727272",
    /** Input adornment icon (mail) */
    iconGrey: "#B4B5B5",
    /** "Remember me" label text */
    rememberMeText: "#595F63",
    /** Unchecked checkbox outline */
    checkboxUnchecked: "#9B9D9F",
    /** 2FA note text */
    noteText: "#74797B",
  },
  fontFamily: PRIMITIVE.typography.fontFamily,
  fontWeight: PRIMITIVE.typography.fontWeight,
  fontSize: {
    /** Welcome heading */
    heading: "22px",
    /** Body copy (subheading, labels, note) */
    body: "14px",
    /** Primary button text */
    button: "16px",
    /** Checkbox icon */
    checkboxIcon: 20,
    /** Small inline icon (e.g. mail adornment) */
    iconSm: 16,
  },
  height: "52px",
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// SCREEN TOKENS — Site unreachable fallback (login group API failure)
// ═══════════════════════════════════════════════════════════════════════════════

export const SITE_UNREACHABLE_TOKENS = {
  color: {
    /** Near-black heading */
    heading: PRIMITIVE.color.neutrals[2500], // #1A1A1A
    /** Body copy + helper text */
    body: PRIMITIVE.color.neutrals[2100], // #666666
    /** Error code (smaller, muted) */
    errorCode: PRIMITIVE.color.neutrals[1900], // #808080
    /** Broken-page icon stroke/fill */
    icon: PRIMITIVE.color.neutrals[1700], // #9E9E9E
    /** Link + primary button bg */
    primary: PRIMITIVE.color.brandBlue[700], // #104E84
    /** Primary button hover bg */
    primaryHover: PRIMITIVE.color.brandBlue[800], // #0B406E
    /** Button label */
    buttonText: PRIMITIVE.color.neutrals["000"], // #FFFFFF
    /** Page background */
    background: PRIMITIVE.color.neutrals["000"], // #FFFFFF
  },
  fontFamily: PRIMITIVE.typography.fontFamily,
  fontWeight: PRIMITIVE.typography.fontWeight,
  fontSize: {
    heading: `${PRIMITIVE.typography.fontSize.heading.h3}px`, // 28px
    body: `${PRIMITIVE.typography.fontSize.body.b3}px`, // 16px
    errorCode: `${PRIMITIVE.typography.fontSize.body.b5}px`, // 12px
    button: `${PRIMITIVE.typography.fontSize.body.b4}px`, // 14px
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Pixel string helper: `px(8)` → `"8px"` */
export const px = (v: number) => `${v}px`;

